/**
 * @file routes.ts
 *
 * @fileoverview Helper functions to support client side routing. Someitmes routes that
 * come from the CSS need some processing or check before calling the "Link" component due
 * to the way that nextjs expects the ref sent to Link.
 */

import { useContext } from 'react';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';
import { QueryFilter } from '~/jobiqo/models/search';
import { drupalRelativePath } from './links';

/**
 * Checks if a route is from a landing page.
 *
 * @todo Ideally this will not be needed when we can just call href with
 * any route using nextjs. see https://github.com/vercel/next.js/issues/8207
 *
 * @param url
 * The url to check.
 */
export const checkIsLandingPage = (url: string) => {
  const nonLandingPagePaths =
    getCurrentFrontendConfig().APP.NON_LANDING_PAGE_PATHS ?? [];
  return !nonLandingPagePaths.some((path) => url.startsWith(path));
};

/**
 * Gets the right href for a Next.js Link since landing pages have to be linked to their
 * respective page (/router)
 *
 * @param path
 *   The path to render a link for.
 *
 * @returns The Next.js link href.
 */
export const getFormattedPageLink = (path): { href: any; as?: string } => {
  const removedInternalPath = drupalRelativePath(path);
  return checkIsLandingPage(removedInternalPath) &&
    removedInternalPath !== '/' &&
    path.substring(0, 4) !== 'http'
    ? {
        href: {
          pathname: '/router',
          query: { router: removedInternalPath },
        },
        as: removedInternalPath,
      }
    : {
        href: removedInternalPath,
      };
};

/**
 * Checks if a canonical link is provided by child element.
 *
 * @param url
 * The url to check.
 * @param pathname
 * The current pathname (next.js internal path name)
 */
export const isCanonicalProvidedByChild = (
  url: string,
  pathname: string,
): boolean => {
  var searchParams = new URLSearchParams(url.split('?').pop());

  // Default job search meta tags (not coming from Drupal) are handled in the
  // JobSearchMetatags component.
  return (
    pathname === '/jobs/[[...taxonomy]]' ||
    searchParams.getAll('job_organization_name').length === 1 ||
    searchParams.has('jobId')
  );
};

/**
 * Constructs filter for query from the current querystring.
 *
 * @param querystring
 *  The current query string
 * @param {string[]} fields
 *  The filters fields.
 * @returns { QueryFilter[] }
 *  Filters array as a object.
 */
export function getQueryFilters(
  querystring,
  fields: string[] = [],
): QueryFilter[] {
  const filters: QueryFilter[] = [];

  // Search value.
  if (querystring.filter) {
    filters.push({
      field: 'title',
      value: querystring.filter,
      operator: 'LIKE',
    });
  }

  fields.forEach((field) => {
    if (querystring[field]) {
      // We want an exact match for the job workflow
      // state, so we do not define the operator here.
      filters.push({
        field,
        value: querystring[field],
      });
    }
  });

  return filters;
}

/**
 * Determines the current page type based on url.
 *
 * @returns string
 *  Example of possible values:
 *    - frontpage
 *    - jobs
 *    - job
 *    - organizations
 *    - organization
 *    - resumes
 *    - resume
 *    - user-login
 *    - user-register-recruiter
 *    - user-register-applicant
 *    - dashboard
 *    - etc.
 */
export const getCurrentPageType = (): string => {
  const { pathname }: PathContextInterface = useContext(PathContext);
  let pathnameNew = pathname;
  // Remove the square brackets.
  pathnameNew = pathnameNew.replace(/(\[.+\])/g, '');
  // Replace slashes with dashes.
  pathnameNew = pathnameNew.replace(/\//g, '-');
  // Remove dash from begging of string.
  pathnameNew = pathnameNew.replace(/^-/, '');
  // Remove dash from end of string.
  pathnameNew = pathnameNew.replace(/-$/, '');
  if (pathnameNew == '') {
    pathnameNew = 'frontpage';
  }
  return pathnameNew;
};

/**
 * Cleans the resolved url from the query params and
 * the nextjs injected query params.
 * @param {string} url The url to clean.
 * @returns {string} The cleaned url.
 */
export function removeNextJsQueryParams(url: string) {
  const urlParts = url.split('?');
  let searchParams = new URLSearchParams(urlParts[1]);

  // Remove the nextjs injected query params.
  searchParams.delete('router');

  return `${urlParts[0]}${
    searchParams.toString() ? `?${searchParams.toString()}` : ''
  }`;
}
